import { useQuery } from '@tanstack/react-query';
import type { ZohoDealFreshness, ZohoDealReadResponse } from '@pazportal/types';
import {
  ZOHO_DEAL_FRESHNESS_MS,
  ZOHO_DEAL_STALE_MS,
} from '@pazportal/types';
import { supabase } from '../lib/supabase';

export type ZohoDealLayoutRequest = 'field_deployment';

/**
 * §D read path — pull-on-view (enabled mount) + 60s background poll.
 * Freshness thresholds are fixed here; consumers must not override (Paul §2).
 */
export function useZohoFdDeal(supabaseTripId: string | undefined) {
  return useQuery({
    queryKey: ['zoho-deal', 'field_deployment', supabaseTripId],
    enabled: Boolean(supabaseTripId),
    staleTime: ZOHO_DEAL_FRESHNESS_MS,
    refetchInterval: ZOHO_DEAL_FRESHNESS_MS,
    queryFn: async (): Promise<ZohoDealReadResponse> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${baseUrl}/functions/v1/zoho-deal-read`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          layout: 'field_deployment',
          supabaseTripId,
        }),
      });

      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error ?? `Zoho deal read failed (${res.status})`);
      }

      return (await res.json()) as ZohoDealReadResponse;
    },
  });
}

/** Freshness props derived from the deal fetcher — pass through to FreshnessIndicator. */
export function zohoDealFreshnessProps(
  freshness: ZohoDealFreshness | undefined,
  label = 'Zoho CRM',
) {
  return {
    lastUpdatedAt: freshness?.modifiedTime ?? freshness?.fetchedAt ?? null,
    expectedRefreshMs: ZOHO_DEAL_FRESHNESS_MS as number,
    staleMs: ZOHO_DEAL_STALE_MS as number,
    label,
  };
}
